import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store';
import {
  getDeviceSharingRequestsRequest,
  filterDeviceSharingRequestsRequest,
  filterDeviceSharingRequestsCleanup,
  postDeviceSharingActionCleanup
} from 'src/features/shared-access/shared-access-slice';
import { getDeviceTelemetryRequest } from 'src/features/telemetry/device-telemetry-slice';
import { DeviceTelemetryStatus } from 'src/types/device-telemetry';
import { DeviceSharingRequest } from 'src/types/devices';
import { isRequestNotExpired } from 'src/utils/is-request-not-expired';

import useDeviceSharingActionToast from 'src/hooks/useDeviceSharingActionToast';

import SharedDevicesTable from './shared-devices-table';
import SharedWithMeTable from './shared-with-me-table';
import { Divider } from '../common';
import { SharedAccessContainer } from './styled';
import { useTheme } from '@veneer/core';

export type SharedAccessProps = {
  isActive?: boolean;
};

const SharedAccess: React.FC<SharedAccessProps> = (
  props: SharedAccessProps
) => {
  const theme = useTheme();
  const { isActive = false } = props;
  const dispatch = useDispatch();

  const {
    data: sharedByMeData,
    status: sharedByMeStatus,
    error: sharedByMeError
  } = useSelector(
    (state: RootState) => state.SharedAccessReducer.getDeviceSharingRequests
  );

  const {
    data: sharedWithMeData,
    status: sharedWithMeStatus,
    error: sharedWithMeError
  } = useSelector(
    (state: RootState) => state.SharedAccessReducer.filterDeviceSharingRequests
  );

  const postDeviceSharingActionState = useSelector(
    (state: RootState) => state.SharedAccessReducer.postDeviceSharingAction
  );

  const deviceTelemetry = useSelector(
    (state: RootState) => state.DeviceTelemetryReducer.deviceTelemetry
  );

  const sharedWithMeFiltered = useMemo(() => {
    if (!sharedWithMeData) return sharedWithMeData;
    return sharedWithMeData.filter((request: DeviceSharingRequest) => {
      if (isRequestNotExpired(request)) return true;
      const telemetry =
        deviceTelemetry?.[request.device.device_type]?.[
          request.device.device_serial_nr
        ];
      const hasCompletedTelemetry =
        (telemetry?.[DeviceTelemetryStatus.COMPLETED]?.length ?? 0) > 0;
      const hasQueuedTelemetry =
        (telemetry?.[DeviceTelemetryStatus.QUEUED]?.length ?? 0) > 0;
      return hasCompletedTelemetry || hasQueuedTelemetry;
    });
  }, [sharedWithMeData, deviceTelemetry]);

  useDeviceSharingActionToast(postDeviceSharingActionState);

  useEffect(() => {
    if (isActive) {
      dispatch(getDeviceSharingRequestsRequest());
      dispatch(filterDeviceSharingRequestsRequest());
      dispatch(getDeviceTelemetryRequest());
    }
    return () => {
      if (isActive) {
        dispatch(filterDeviceSharingRequestsCleanup());
        dispatch(postDeviceSharingActionCleanup());
      }
    };
  }, [isActive, dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(getDeviceSharingRequestsRequest());
  }, [dispatch]);

  const handleSharedWithMeRetry = useCallback(() => {
    dispatch(filterDeviceSharingRequestsRequest());
  }, [dispatch]);

  return (
    <SharedAccessContainer
      data-testid="react-device-management-mfe-shared-access"
      $veneerTheme={theme}
    >
      <SharedDevicesTable
        devices={sharedByMeData}
        status={sharedByMeStatus}
        error={sharedByMeError}
        onRetry={handleRetry}
      />

      <Divider />

      <SharedWithMeTable
        devices={sharedWithMeFiltered}
        status={sharedWithMeStatus}
        error={sharedWithMeError}
        onRetry={handleSharedWithMeRetry}
      />

      <Divider />
    </SharedAccessContainer>
  );
};

export default SharedAccess;
